import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { useTimezone } from "@/hooks/useTimezone";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, EyeOff, Check, Globe, User, Cpu, Terminal, Zap, Info } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api-client";
import { useProfile } from "@/hooks/useProfile";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";

const COMMON_TIMEZONES = [
  "UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Sao_Paulo", "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Moscow",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Shanghai", "Asia/Tokyo", "Asia/Singapore",
  "Australia/Sydney", "Pacific/Auckland", "Africa/Cairo", "Africa/Johannesburg",
];

export function GeneralSettings() {
  const { data: profile } = useProfile();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [defaultInterval, setDefaultInterval] = useState("5");
  const [modeLoading, setModeLoading] = useState(false);
  const { theme, setTheme } = useTheme();
  const { timezone, setTimezone } = useTimezone();

  useEffect(() => {
    if (profile?.default_check_interval) {
        setDefaultInterval(profile.default_check_interval.toString());
    }
  }, [profile?.default_check_interval]);

  const handleModeChange = async (mode: "developer" | "common") => {
    if (profile?.user_mode === mode) return;
    setModeLoading(true);
    try {
      await api.updateProfile({ user_mode: mode });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success(`Tactical Interface: ${mode.toUpperCase()} Mode Active`);
    } catch {
      toast.error("Failed to update interface signal");
    } finally {
      setModeLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword.trim()) return;
    try {
      // Note: In zero-cost, password change happens via Supabase Auth
      const { error } = await api.request<any>('/user/password', {
        method: 'PATCH',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (error) throw new Error(error);
      
      setPasswordUpdated(true);
      toast.success("Identity Secret Updated");
      setTimeout(() => {
        setPasswordUpdated(false);
        setCurrentPassword("");
        setNewPassword("");
      }, 2000);
    } catch (err: any) {
      toast.error(err.message || "Failed to update secret");
    }
  };

  const handleUpdateInterval = async (val: string) => {
    setDefaultInterval(val);
    try {
      await api.updateProfile({ default_check_interval: parseInt(val) });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Default Frequency Updated");
    } catch {
      toast.error("Failed to update heartbeat baseline");
    }
  };

  const handleTimezoneChange = async (tz: string) => {
    try {
      await setTimezone(tz);
      toast.success(`Temporal Sync: ${tz}`);
    } catch {
      toast.error("Temporal sync failed");
    }
  };

  return (
    <div className="space-y-12">
      {/* User Identity Section */}
      <div className="space-y-6">
        <div>
          <h3 className="text-xs font-black uppercase tracking-[0.2em] text-primary mb-1">User Identity</h3>
          <p className="text-[10px] font-bold text-muted-foreground uppercase leading-relaxed">
            Your authenticated credentials and system profile.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 p-4 border-4 border-foreground bg-accent/10">
          <div className="relative h-20 w-20 shrink-0 border-4 border-foreground bg-background overflow-hidden rounded-none shadow-brutal-sm">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.name || "User Profile"}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center font-mono text-2xl font-black uppercase bg-muted text-muted-foreground">
                {user?.email ? user.email.slice(0, 2).toUpperCase() : "U"}
              </div>
            )}
          </div>
          <div className="space-y-3 min-w-0 flex-1 w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <span className="text-[8px] font-black uppercase tracking-widest text-muted-foreground">User Name</span>
                <span className="text-sm font-black font-mono text-foreground truncate mt-0.5">
                  {user?.name || user?.email?.split("@")[0] || "Acme Sentinel"}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-[8px] font-black uppercase tracking-widest text-muted-foreground">Email Address</span>
                <span className="text-sm font-bold font-mono text-foreground truncate mt-0.5">
                  {user?.email || "unknown@watchora.com"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-0.5 w-full bg-foreground/10" />

      {/* Interface Profile */}
      <div className="space-y-6">
        <div>
          <h3 className="text-xs font-black uppercase tracking-[0.2em] text-primary mb-1">Operational Profile</h3>
          <p className="text-[10px] font-bold text-muted-foreground uppercase leading-relaxed">
            Switch between technical telemetry and simplified operations.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            disabled={modeLoading}
            onClick={() => handleModeChange("common")}
            className={`flex-1 flex flex-col items-start p-4 border-4 border-foreground transition-all relative overflow-hidden ${
              profile?.user_mode === 'common' 
                ? "bg-primary text-primary-foreground shadow-none translate-x-1 translate-y-1" 
                : "bg-background text-foreground shadow-brutal hover:shadow-none hover:translate-x-1 hover:translate-y-1"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <User className="h-4 w-4" />
              <span className="text-xs font-black uppercase tracking-tighter">Common Mode</span>
            </div>
            <p className="text-[9px] font-bold opacity-70 text-left uppercase leading-tight">Empathetic reporting, simple status pages, and plain-English diagnostics.</p>
            {profile?.user_mode === 'common' && (
              <div className="absolute top-1 right-1">
                <Check className="h-3 w-3" />
              </div>
            )}
          </button>

          <button
            disabled={modeLoading}
            onClick={() => handleModeChange("developer")}
            className={`flex-1 flex flex-col items-start p-4 border-4 border-foreground transition-all relative overflow-hidden ${
              profile?.user_mode === 'developer' 
                ? "bg-foreground text-background shadow-none translate-x-1 translate-y-1" 
                : "bg-background text-foreground shadow-brutal hover:shadow-none hover:translate-x-1 hover:translate-y-1"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Cpu className="h-4 w-4" />
              <span className="text-xs font-black uppercase tracking-tighter">Developer Mode</span>
            </div>
            <p className="text-[9px] font-bold opacity-70 text-left uppercase leading-tight">Low-level logs, deep telemetry, and autonomous auto-restart webhook triggers.</p>
            {profile?.user_mode === 'developer' && (
              <div className="absolute top-1 right-1">
                <Check className="h-3 w-3" />
              </div>
            )}
          </button>
        </div>
      </div>

      <div className="h-0.5 w-full bg-foreground/10" />

      {/* Timezone */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-primary" />
          <h3 className="text-xs font-black uppercase tracking-widest">Temporal Synchronization</h3>
        </div>
        <p className="text-[10px] font-bold text-muted-foreground uppercase">All check logs and incident reports will adapt to this master clock.</p>
        <Select value={timezone} onValueChange={handleTimezoneChange}>
          <SelectTrigger className="h-12 w-full sm:w-80 rounded-none border-4 border-foreground bg-background font-black uppercase tracking-tighter shadow-brutal-sm focus:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none border-4 border-foreground max-h-60">
            {COMMON_TIMEZONES.map((tz) => (
              <SelectItem key={tz} value={tz} className="text-[10px] font-black uppercase">{tz.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="h-0.5 w-full bg-foreground/10" />

      {/* Default Check Interval */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" />
          <h3 className="text-xs font-black uppercase tracking-widest">Heartbeat Frequency</h3>
        </div>
        <p className="text-[10px] font-bold text-muted-foreground uppercase">Default resolution for new perimeter probes. Individual sites can override this.</p>
        <Select value={defaultInterval} onValueChange={handleUpdateInterval}>
          <SelectTrigger className="h-12 w-full sm:w-80 rounded-none border-4 border-foreground bg-background font-black uppercase tracking-tighter shadow-brutal-sm focus:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none border-4 border-foreground">
            <SelectItem value="1" className="text-[10px] font-black uppercase italic">High-Tactical (1m)</SelectItem>
            <SelectItem value="5" className="text-[10px] font-black uppercase">Standard Pulse (5m)</SelectItem>
            <SelectItem value="15" className="text-[10px] font-black uppercase">Deep Guard (15m)</SelectItem>
            <SelectItem value="30" className="text-[10px] font-black uppercase opacity-60">Archive Mode (30m)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="h-0.5 w-full bg-foreground/10" />

      {/* Theme */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Terminal className="h-4 w-4 text-primary" />
          <h3 className="text-xs font-black uppercase tracking-widest">Terminal Visuals</h3>
        </div>
        <Select value={theme || "system"} onValueChange={setTheme}>
          <SelectTrigger className="h-12 w-full sm:w-80 rounded-none border-4 border-foreground bg-background font-black uppercase tracking-tighter shadow-brutal-sm focus:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none border-4 border-foreground">
            <SelectItem value="system" className="text-[10px] font-black uppercase">System OS</SelectItem>
            <SelectItem value="light" className="text-[10px] font-black uppercase">Operation: Day</SelectItem>
            <SelectItem value="dark" className="text-[10px] font-black uppercase">Operation: Night</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="h-0.5 w-full bg-foreground/10" />

      <div className="bg-accent border-4 border-foreground p-6 shadow-brutal-sm">
        <h4 className="text-[10px] font-black uppercase text-primary mb-2 flex items-center gap-2">
          <Info className="h-3 w-3" /> System Intelligence
        </h4>
        <p className="text-xs font-black uppercase tracking-tighter italic">UptimeWing Platform v3.0 // Unified Command</p>
        <p className="text-[10px] font-bold text-muted-foreground uppercase leading-tight mt-2">
          Powered by Global Edge Consensus and Neutral Forensic Analysis. 
          Your terminal is securely tethered to the master blockchain.
        </p>
      </div>
    </div>
  );
}
